import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  Divider,
  Grid,
  Card,
  CardContent,
  useTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  Home as HomeIcon,
  LocationOn as LocationIcon,
  AttachMoney as PriceIcon,
  Hotel as BedIcon,
  Bathtub as BathIcon,
  Garage as GarageIcon,
  SquareFoot as AreaIcon,
  CalendarToday as CalendarIcon,
  Category as CategoryIcon,
  Visibility as FeaturedIcon,
  Star as BestIcon,
  Map as MapIcon,
  VideoLibrary as VideoIcon,
  CheckCircle as CheckIcon,
  ThreeDRotation as ThreeDIcon
} from '@mui/icons-material';
import FloorPlanIcon from '@mui/icons-material/BedroomParent';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DescriptionIcon from '@mui/icons-material/Description';

import palette from 'src/theme/palette';
import { useState, useEffect } from 'react';
import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';
import useMediaQuery from '@mui/material/useMediaQuery';

// Download handler ko improve karein
const handlePdfDownload = (event, fileUrl, fileName) => {
  event.preventDefault();

  // Create temporary link
  const link = document.createElement('a');
  link.href = fileUrl;
  link.download = fileName || 'document.pdf';
  link.target = '_blank';

  // Trigger download
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const PropertyViewModal = ({ open, onClose, property }) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const theme = useTheme();

  useEffect(() => {
    Fancybox.bind('[data-fancybox]', {});
    return () => {
      Fancybox.destroy();
    };
  }, []);

  if (!property) return null;

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'sold':
        return 'error';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  // Helper function to get available for label
  const getAvailableForLabel = (propertyStatus) => {
    if (!propertyStatus || propertyStatus === '0') return 'N/A';
    switch (propertyStatus.toLowerCase()) {
      case 'sale':
        return 'Sale';
      case 'rent':
        return 'Rent';
      case 'sold':
        return 'Sold';
      default:
        return propertyStatus;
    }
  };

  // Format price with commas
  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return `$${parseFloat(price).toLocaleString()}`;
  };

  // Format area size
  const formatArea = (area, areaUnit = null) => {
    if (!area) return 'N/A';
    const unit = areaUnit?.name || 'NA';
    return `${area.toLocaleString()} ${unit}`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Categorize features
  // const categorizeFeatures = (features) => {
  //   if (!features || !Array.isArray(features)) return {};

  //   const categories = {
  //     outdoor: [],
  //     indoor: [],
  //     amenities: [],
  //     utilities: [],
  //     safety: [],
  //     accessibility: [],
  //     other: []
  //   };

  //   const outdoorKeywords = ['yard', 'garden', 'pool', 'back yard', 'front yard'];
  //   const indoorKeywords = ['kitchen', 'laundry', 'media room', 'fireplace'];
  //   const amenitiesKeywords = ['gym', 'hot bath', 'garage'];
  //   const utilitiesKeywords = [
  //     'air',
  //     'electricity',
  //     'heating',
  //     'gas',
  //     'ventilation',
  //     'water',
  //     'wifi'
  //   ];
  //   const safetyKeywords = ['smoke', 'detector', 'fire'];
  //   const accessibilityKeywords = ['chair', 'elevator', 'accessible'];

  //   features.forEach((feature) => {
  //     const featureName = feature.name.toLowerCase();

  //     if (outdoorKeywords.some((keyword) => featureName.includes(keyword))) {
  //       categories.outdoor.push(feature);
  //     } else if (indoorKeywords.some((keyword) => featureName.includes(keyword))) {
  //       categories.indoor.push(feature);
  //     } else if (amenitiesKeywords.some((keyword) => featureName.includes(keyword))) {
  //       categories.amenities.push(feature);
  //     } else if (utilitiesKeywords.some((keyword) => featureName.includes(keyword))) {
  //       categories.utilities.push(feature);
  //     } else if (safetyKeywords.some((keyword) => featureName.includes(keyword))) {
  //       categories.safety.push(feature);
  //     } else if (accessibilityKeywords.some((keyword) => featureName.includes(keyword))) {
  //       categories.accessibility.push(feature);
  //     } else {
  //       categories.other.push(feature);
  //     }
  //   });

  //   // Remove empty categories
  //   Object.keys(categories).forEach((key) => {
  //     if (categories[key].length === 0) {
  //       delete categories[key];
  //     }
  //   });

  //   return categories;
  // };

  // // Get category display name
  // const getCategoryDisplayName = (category) => {
  //   const categoryNames = {
  //     outdoor: 'Outdoor Features',
  //     indoor: 'Indoor Features',
  //     amenities: 'Amenities',
  //     utilities: 'Utilities',
  //     safety: 'Safety & Security',
  //     accessibility: 'Accessibility',
  //     other: 'Other Features'
  //   };
  //   return categoryNames[category] || category;
  // };

  // Get images to display (first 4 images)
  // const getDisplayImages = () => {
  //   if (!property.images || property.images.length === 0) return [];
  //   return property.images.slice(0, 4);
  // };

  // // Get remaining images count
  // const getRemainingImagesCount = () => {
  //   if (!property.images) return 0;
  //   return Math.max(0, property.images.length - 4);
  // };

  // const displayImages = getDisplayImages();
  // const remainingCount = getRemainingImagesCount();
  // const categorizedFeatures = categorizeFeatures(property.features);

  // PropertyViewModal.js

  const getDisplayImages = () => {
    const allImages = [];

    if (property.main_image) {
      allImages.push(property.main_image);
    }

    if (property.images && Array.isArray(property.images)) {
      const otherImages = property.images.filter((img) => img !== property.main_image);
      allImages.push(...otherImages);
    }

    return allImages.slice(0, 4);
  };

  const getRemainingImagesCount = () => {
    const allImages = [];

    if (property.main_image) {
      allImages.push(property.main_image);
    }

    if (property.images && Array.isArray(property.images)) {
      const otherImages = property.images.filter((img) => img !== property.main_image);
      allImages.push(...otherImages);
    }

    return Math.max(0, allImages.length - 4);
  };

  const getAllImagesForGallery = () => {
    const allImages = [];

    if (property.main_image) {
      allImages.push(property.main_image);
    }

    if (property.images && Array.isArray(property.images)) {
      const otherImages = property.images.filter((img) => img !== property.main_image);
      allImages.push(...otherImages);
    }

    return allImages;
  };

  // Update in the render section:
  const displayImages = getDisplayImages();
  const remainingCount = getRemainingImagesCount();
  const allImages = getAllImagesForGallery();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          margin: { xs: 0, sm: 3 },
          maxHeight: isMobile ? '100vh' : '95vh',
          minHeight: '80vh',
          width: isMobile ? '100vw' : '1400px'
        }
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: palette.primary.main,
          color: palette.primary.contrastText,
          py: 2.5,
          position: 'relative',
          borderTopLeftRadius: { xs: 0, sm: 8 },
          borderTopRightRadius: { xs: 0, sm: 8 }
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'poppinsSemiBold',
            color: palette.secondary.contrastText,
            fontSize: '20px',
            pr: 6
          }}
        >
          Property Details - {property?.title}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: palette.secondary.contrastText,
            backgroundColor: 'rgba(255,255,255,0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, mt: 2 }}>
        {/* Property Images & Basic Info */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Main Image and Gallery */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}
            >
              <CardContent sx={{ p: 2 }}>
                {/* Main Large Image */}
                <Box
                  sx={{
                    height: 300,
                    backgroundColor: palette.grey[200],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 2,
                    overflow: 'hidden',
                    mb: 2,
                    cursor: displayImages.length > 0 ? 'pointer' : 'default'
                  }}
                >
                  {displayImages.length > 0 ? (
                    <a
                      href={displayImages[0]}
                      data-fancybox="property-gallery"
                      data-caption={property.title}
                    >
                      <img
                        src={displayImages[0]}
                        alt={property.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    </a>
                  ) : null}
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: displayImages.length > 0 ? 'none' : 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: palette.grey[300],
                      flexDirection: 'column'
                    }}
                  >
                    <HomeIcon sx={{ fontSize: 60, color: palette.text.disabled, mb: 1 }} />
                    <Typography variant="body2" color="textSecondary">
                      No Image Available
                    </Typography>
                  </Box>
                </Box>

                {/* Thumbnail Images Grid */}
                {displayImages.length > 1 && (
                  <Grid container spacing={1}>
                    {displayImages.slice(1).map((image, index) => (
                      <Grid item xs={4} key={index}>
                        {index === 2 && remainingCount > 0 ? (
                          <Box
                            sx={{
                              height: 100,
                              backgroundColor: palette.grey[200],
                              borderRadius: 1,
                              overflow: 'hidden',
                              cursor: 'pointer',
                              position: 'relative',
                              '&:hover': {
                                transform: 'scale(1.02)',
                                transition: 'transform 0.2s ease'
                              }
                            }}
                          >
                            <a
                              href={image}
                              data-fancybox="property-gallery"
                              data-caption={property.title}
                              style={{
                                display: 'block',
                                width: '100%',
                                height: '100%',
                                position: 'relative'
                              }}
                            >
                              <img
                                src={image}
                                alt={`${property.title} - Thumbnail ${index + 2}`}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  filter: 'brightness(0.7)'
                                }}
                              />
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: 'rgba(0,0,0,0.5)',
                                  color: 'white'
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontFamily: 'poppinsSemiBold',
                                    fontSize: '16px'
                                  }}
                                >
                                  +{remainingCount}
                                </Typography>
                              </Box>
                            </a>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              height: 100,
                              backgroundColor: palette.grey[200],
                              borderRadius: 1,
                              overflow: 'hidden',
                              cursor: 'pointer',
                              position: 'relative',
                              '&:hover': {
                                transform: 'scale(1.02)',
                                transition: 'transform 0.2s ease'
                              }
                            }}
                          >
                            <a
                              href={image}
                              data-fancybox="property-gallery"
                              data-caption={property.title}
                              style={{ display: 'block', height: '100%' }}
                            >
                              <img
                                src={image}
                                alt={`${property.title} - Thumbnail ${index + 2}`}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                              />
                            </a>
                          </Box>
                        )}
                      </Grid>
                    ))}
                  </Grid>
                )}
                {console.log(property?.main_image, 'main image')}

                {property.images && property.images.length > 4 && (
                  <div style={{ display: 'none' }}>
                    {property.images.slice(4).map((image, index) => (
                      <a
                        key={index + 4}
                        href={image}
                        data-fancybox="property-gallery"
                        data-caption={property.title}
                      >
                        <img src={image} alt={`${property.title} - Image ${index + 5}`} />
                      </a>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Basic Information */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: 'poppinsSemiBold',
                    color: palette.text.primary,
                    mb: 2,
                    fontSize: '24px'
                  }}
                >
                  {property.title}
                </Typography>

                {/* Address */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <LocationIcon
                    sx={{
                      color: palette.primary.main,
                      mr: 1.5,
                      mt: 0.5,
                      fontSize: '20px'
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsMedium',
                        color: palette.text.secondary,
                        fontSize: '14px'
                      }}
                    >
                      Address
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsRegular',
                        color: palette.text.primary,
                        fontSize: '15px',
                        lineHeight: 1.4
                      }}
                    >
                      {property.address || 'N/A'}
                    </Typography>
                  </Box>
                </Box>

                {/* Price */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PriceIcon
                    sx={{
                      color: palette.primary.main,
                      mr: 1.5,
                      fontSize: '20px'
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsMedium',
                        color: palette.text.secondary,
                        fontSize: '14px'
                      }}
                    >
                      Price
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsSemiBold',
                        color: palette.primary.main,
                        fontSize: '20px'
                      }}
                    >
                      {formatPrice(property.price)}
                      {['1', 1].includes(property?.is_area_unit) && (
                        <> / {property?.area_unit?.name || 'unit'}</>
                      )}
                    </Typography>
                  </Box>
                </Box>

                {/* Property Stats */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        p: 1.5,
                        backgroundColor: palette.grey[50],
                        borderRadius: 2
                      }}
                    >
                      <BedIcon sx={{ color: palette.primary.main, mb: 0.5 }} />
                      <Typography sx={{ fontFamily: 'poppinsSemiBold', fontSize: '16px' }}>
                        {property.bedrooms || 0}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'poppinsRegular',
                          fontSize: '12px',
                          color: palette.text.secondary
                        }}
                      >
                        Bedrooms
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        p: 1.5,
                        backgroundColor: palette.grey[50],
                        borderRadius: 2
                      }}
                    >
                      <BathIcon sx={{ color: palette.primary.main, mb: 0.5 }} />
                      <Typography sx={{ fontFamily: 'poppinsSemiBold', fontSize: '16px' }}>
                        {property.bathrooms || 0}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'poppinsRegular',
                          fontSize: '12px',
                          color: palette.text.secondary
                        }}
                      >
                        Bathrooms
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        p: 1.5,
                        backgroundColor: palette.grey[50],
                        borderRadius: 2
                      }}
                    >
                      <AreaIcon sx={{ color: palette.primary.main, mb: 0.5 }} />
                      <Typography sx={{ fontFamily: 'poppinsSemiBold', fontSize: '16px' }}>
                        {formatArea(property.area_size, property?.area_unit)}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'poppinsRegular',
                          fontSize: '12px',
                          color: palette.text.secondary
                        }}
                      >
                        Area
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        p: 1.5,
                        backgroundColor: palette.grey[50],
                        borderRadius: 2
                      }}
                    >
                      <GarageIcon sx={{ color: palette.primary.main, mb: 0.5 }} />
                      <Typography sx={{ fontFamily: 'poppinsSemiBold', fontSize: '16px' }}>
                        {property.is_garage == 1 ? 'Yes' : 'No'}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'poppinsRegular',
                          fontSize: '12px',
                          color: palette.text.secondary
                        }}
                      >
                        Garage
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Status Chips */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={`Status: ${property.status || 'N/A'}`}
                    color={getStatusColor(property.status)}
                    size="small"
                    sx={{ fontFamily: 'poppinsMedium' }}
                  />
                  <Chip
                    label={`Type: ${property.property_type?.name || 'N/A'}`}
                    variant="outlined"
                    size="small"
                    sx={{ fontFamily: 'poppinsMedium' }}
                  />
                  {property.is_featured === 1 && (
                    <Chip
                      icon={<FeaturedIcon />}
                      label="Featured"
                      color="primary"
                      size="small"
                      sx={{ fontFamily: 'poppinsMedium' }}
                    />
                  )}
                  {property.is_best === 1 && (
                    <Chip
                      icon={<BestIcon />}
                      label="Best Property"
                      color="secondary"
                      size="small"
                      sx={{ fontFamily: 'poppinsMedium' }}
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Description */}
        {property.description && (
          <Box
            sx={{
              height: '100%',
              borderRadius: 2,
              overflow: 'hidden',
              p: 3,
              mb: 3,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              backgroundColor: 'white'
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'poppinsSemiBold',
                color: palette.text.primary,
                mb: 2,
                fontSize: '18px',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <DescriptionIcon fontSize="small" />
              Description
            </Typography>
            <Typography
              variant="body1"
              fontFamily="poppinsRegular"
              sx={{
                mb: 2,
                lineHeight: 1.6,
                color: palette.text.primary
              }}
            >
              {property.description}
            </Typography>
          </Box>
        )}

        {/* Detailed Information */}
        <Grid container spacing={3}>
          {/* Property Details */}
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'poppinsSemiBold',
                    color: palette.text.primary,
                    mb: 3,
                    fontSize: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <HomeIcon fontSize="small" />
                  Property Details
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsMedium',
                        color: palette.text.secondary,
                        fontSize: '14px'
                      }}
                    >
                      Build Year
                    </Typography>
                    <Typography sx={{ fontFamily: 'poppinsSemiBold', fontSize: '15px', mb: 2 }}>
                      {property.year_built || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsMedium',
                        color: palette.text.secondary,
                        fontSize: '14px'
                      }}
                    >
                      Number of cars
                    </Typography>
                    <Typography sx={{ fontFamily: 'poppinsSemiBold', fontSize: '15px', mb: 2 }}>
                      {property.garage_cars || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsMedium',
                        color: palette.text.secondary,
                        fontSize: '14px'
                      }}
                    >
                      Property Status
                    </Typography>
                    <Typography sx={{ fontFamily: 'poppinsSemiBold', fontSize: '15px', mb: 2 }}>
                      {getAvailableForLabel(property.property_status)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsMedium',
                        color: palette.text.secondary,
                        fontSize: '14px'
                      }}
                    >
                      Nearby Events
                    </Typography>
                    <Typography sx={{ fontFamily: 'poppinsSemiBold', fontSize: '15px', mb: 2 }}>
                      {property.show_nearby ? 'Yes' : 'No'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsMedium',
                        color: palette.text.secondary,
                        fontSize: '14px'
                      }}
                    >
                      Allow Video Tour
                    </Typography>
                    <Typography sx={{ fontFamily: 'poppinsSemiBold', fontSize: '15px', mb: 2 }}>
                      {property.video_chat === true ? 'Yes' : 'No'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Location Details */}
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'poppinsSemiBold',
                    color: palette.text.primary,
                    mb: 3,
                    fontSize: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <MapIcon fontSize="small" />
                  Location Details
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsMedium',
                        color: palette.text.secondary,
                        fontSize: '14px'
                      }}
                    >
                      City
                    </Typography>
                    <Typography sx={{ fontFamily: 'poppinsSemiBold', fontSize: '15px', mb: 2 }}>
                      {typeof property.city === 'string' && isNaN(property.city)
                        ? property.city
                        : 'NA'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsMedium',
                        color: palette.text.secondary,
                        fontSize: '14px'
                      }}
                    >
                      State
                    </Typography>
                    <Typography sx={{ fontFamily: 'poppinsSemiBold', fontSize: '15px', mb: 2 }}>
                      {typeof property.state === 'string' && isNaN(property.state)
                        ? property.state
                        : 'NA'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsMedium',
                        color: palette.text.secondary,
                        fontSize: '14px'
                      }}
                    >
                      Country
                    </Typography>
                    <Typography sx={{ fontFamily: 'poppinsSemiBold', fontSize: '15px', mb: 2 }}>
                      {property.country || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsMedium',
                        color: palette.text.secondary,
                        fontSize: '14px'
                      }}
                    >
                      Postal Code
                    </Typography>
                    <Typography sx={{ fontFamily: 'poppinsSemiBold', fontSize: '15px', mb: 2 }}>
                      {property.postal_code || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsMedium',
                        color: palette.text.secondary,
                        fontSize: '14px'
                      }}
                    >
                      Latitude
                    </Typography>
                    <Typography sx={{ fontFamily: 'poppinsSemiBold', fontSize: '15px', mb: 2 }}>
                      {property.lat || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      sx={{
                        fontFamily: 'poppinsMedium',
                        color: palette.text.secondary,
                        fontSize: '14px'
                      }}
                    >
                      Longitude
                    </Typography>
                    <Typography sx={{ fontFamily: 'poppinsSemiBold', fontSize: '15px', mb: 2 }}>
                      {property.lon || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        {/* Features Section */}
        {property.features && property.features.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Card sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'poppinsSemiBold',
                    color: palette.text.primary,
                    mb: 3,
                    fontSize: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <CheckIcon fontSize="small" />
                  Property Features
                  <Chip
                    label={`${property.features.length} categories`}
                    size="small"
                    color="primary"
                    sx={{ ml: 1, height: '24px' }}
                  />
                </Typography>

                {/* Categorized Features - Fixed structure */}
                {property.features.map((categoryGroup, index) => (
                  <Box key={index} sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontFamily: 'poppinsSemiBold',
                        color: palette.primary.main,
                        mb: 2,
                        fontSize: '16px',
                        borderBottom: `2px solid ${palette.primary.main}`,
                        pb: 0.5,
                        display: 'inline-block'
                      }}
                    >
                      {categoryGroup.category_name}
                    </Typography>

                    {categoryGroup.sub_categories &&
                      categoryGroup.sub_categories.map((subCategory, subIndex) => (
                        <Box key={subIndex} sx={{ mb: 2, ml: 2 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontFamily: 'poppinsSemiBold',
                              color: palette.text.primary,
                              mb: 1.5,
                              fontSize: '14px'
                            }}
                          >
                            {subCategory.sub_category_name}
                          </Typography>

                          <Grid container spacing={1}>
                            {subCategory.features &&
                              subCategory.features.map((feature) => (
                                <Grid item xs={12} sm={6} md={4} key={feature.id}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      p: 1.5,
                                      borderRadius: 1,
                                      backgroundColor: palette.grey[50],
                                      border: `1px solid ${palette.grey[200]}`,
                                      transition: 'all 0.2s ease',
                                      '&:hover': {
                                        backgroundColor: palette.grey[100],
                                        transform: 'translateY(-1px)'
                                      }
                                    }}
                                  >
                                    <CheckIcon
                                      sx={{
                                        color: palette.success.main,
                                        mr: 1.5,
                                        fontSize: '18px'
                                      }}
                                    />
                                    <Typography
                                      sx={{
                                        fontFamily: 'poppinsMedium',
                                        color: palette.text.primary,
                                        fontSize: '14px'
                                      }}
                                    >
                                      {feature.name}
                                    </Typography>
                                  </Box>
                                </Grid>
                              ))}
                          </Grid>
                        </Box>
                      ))}
                  </Box>
                ))}
              </CardContent>
            </Card>
          </>
        )}

        {/* Floor Plans Section */}
        {property.floor_plans && property.floor_plans.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Card sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'poppinsSemiBold',
                    color: palette.text.primary,
                    mb: 3,
                    fontSize: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <FloorPlanIcon fontSize="small" />
                  Floor Plans
                  <Chip
                    label={`${property.floor_plans.length} plans`}
                    size="small"
                    color="primary"
                    sx={{ ml: 1, height: '24px' }}
                  />
                </Typography>

                <Grid container spacing={3}>
                  {property?.floor_plans.map((floorPlan, index) => (
                    <Grid item xs={12} md={6} lg={4} key={floorPlan.id || index}>
                      <Card
                        sx={{
                          borderRadius: 2,
                          overflow: 'hidden',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          transition: 'transform 0.2s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
                          }
                        }}
                      >
                        <Box
                          sx={{
                            height: 200,
                            backgroundColor: palette.grey[200],
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            cursor: 'pointer'
                          }}
                        >
                          {floorPlan?.file || floorPlan?.floor_image ? (
                            <a
                              href={floorPlan.file || floorPlan.floor_image}
                              data-fancybox="floor-plans-gallery"
                              data-caption={floorPlan.title || `Floor Plan ${index + 1}`}
                            >
                              <img
                                src={floorPlan.file || floorPlan.floor_image}
                                alt={floorPlan.title || `Floor Plan ${index + 1}`}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            </a>
                          ) : null}
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              display: floorPlan.file || floorPlan.floor_image ? 'none' : 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: palette.grey[300],
                              flexDirection: 'column'
                            }}
                          >
                            <FloorPlanIcon
                              sx={{ fontSize: 40, color: palette.text.disabled, mb: 1 }}
                            />
                            <Typography variant="body2" color="textSecondary">
                              No Floor Plan Image
                            </Typography>
                          </Box>
                        </Box>

                        <CardContent sx={{ p: 2 }}>
                          <Typography
                            sx={{
                              fontFamily: 'poppinsSemiBold',
                              color: palette.text.primary,
                              fontSize: '16px',
                              mb: 1
                            }}
                          >
                            {floorPlan.title || `Floor ${index + 1}`}
                          </Typography>

                          {floorPlan.description && (
                            <Typography
                              sx={{
                                fontFamily: 'poppinsRegular',
                                color: palette.text.secondary,
                                fontSize: '14px',
                                mb: 2,
                                lineHeight: 1.4
                              }}
                            >
                              {floorPlan.description}
                            </Typography>
                          )}

                          <Grid container spacing={1} sx={{ mb: 1 }}>
                            <Grid item xs={6}>
                              <Typography
                                sx={{
                                  fontFamily: 'poppinsMedium',
                                  color: palette.text.secondary,
                                  fontSize: '12px'
                                }}
                              >
                                Bedrooms
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: 'poppinsSemiBold',
                                  color: palette.text.primary,
                                  fontSize: '14px'
                                }}
                              >
                                {floorPlan.bedrooms || 0}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography
                                sx={{
                                  fontFamily: 'poppinsMedium',
                                  color: palette.text.secondary,
                                  fontSize: '12px'
                                }}
                              >
                                Bathrooms
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: 'poppinsSemiBold',
                                  color: palette.text.primary,
                                  fontSize: '14px'
                                }}
                              >
                                {floorPlan.bathrooms || 0}
                              </Typography>
                            </Grid>
                          </Grid>

                          <Grid container spacing={1} sx={{ mb: 1 }}>
                            <Grid item xs={6}>
                              <Typography
                                sx={{
                                  fontFamily: 'poppinsMedium',
                                  color: palette.text.secondary,
                                  fontSize: '12px'
                                }}
                              >
                                Size
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: 'poppinsSemiBold',
                                  color: palette.text.primary,
                                  fontSize: '14px'
                                }}
                              >
                                {floorPlan.size
                                  ? `${floorPlan.size.toLocaleString()} ${
                                      property?.area_unit?.name || 'NA'
                                    }`
                                  : 'N/A'}
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </>
        )}

        <Grid container spacing={3} sx={{ mt: 2, mb: 2, px: 4 }}>
          {/* Video Section */}
          <Grid item xs={12} md={6}>
            {property?.video && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'poppinsSemiBold',
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 2
                  }}
                >
                  <VideoIcon color="primary" />
                  Property Video
                </Typography>
                <Box
                  sx={{
                    height: 400,
                    backgroundColor: palette.grey[200],
                    borderRadius: 2,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <video
                    controls
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    poster={property?.video?.thumbnail || null}
                  >
                    <source src={property?.video?.file} type="video/webm" />
                    Your browser does not support the video tag.
                  </video>
                </Box>
              </Box>
            )}
          </Grid>

          {/* 3D Model Section */}
          <Grid item xs={12} md={6}>
            {property.view_3d && (
              <Box sx={{ mb: 3, mt: 2 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'poppinsSemiBold',
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <ThreeDIcon color="primary" />
                  3D Model
                </Typography>
                <Box
                  sx={{
                    height: 400,
                    width: '100%',
                    backgroundColor: palette.grey[200],
                    borderRadius: 2,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <iframe
                    src={property.view_3d}
                    width="100%"
                    height="400"
                    style={{ border: 'none' }}
                  />
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mt: 2, mb: 2, px: 4 }}>
          {/* Drone Section */}
          <Grid item xs={12} md={6}>
            {property.drone_video && property.drone_video.file && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'poppinsSemiBold',
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 2
                  }}
                >
                  <VideoIcon color="primary" />
                  Property Drone Video
                </Typography>
                <Box
                  sx={{
                    height: 400,
                    backgroundColor: palette.grey[200],
                    borderRadius: 2,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <video
                    controls
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    poster={property.drone_video_thumbnail?.file || null}
                  >
                    <source src={property.drone_video.file} type="video/webm" />
                    Your browser does not support the video tag.
                  </video>
                </Box>
              </Box>
            )}
          </Grid>

          {/* Attachments Section */}
          <Grid item xs={12} md={6}>
            {property.attachments && property.attachments.length > 0 && (
              <Box sx={{ mb: 3, mt: 2 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'poppinsSemiBold',
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <AttachFileIcon color="primary" />
                  Property Attachments ({property.attachments.length})
                </Typography>
                <Box
                  sx={{
                    maxHeight: 400,
                    width: '100%',
                    backgroundColor: palette.grey[200],
                    borderRadius: 2,
                    overflow: 'auto',
                    p: 2
                  }}
                >
                  {property.attachments.map((attachment, index) => (
                    <Box
                      key={attachment.id}
                      sx={{
                        mb: 2,
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        backgroundColor: 'white',
                        borderRadius: 1,
                        boxShadow: 1
                      }}
                    >
                      <Typography variant="body1" sx={{ flex: 1 }}>
                        {attachment.name || `Attachment ${index + 1}`}
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        component="a"
                        href={attachment.file}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(event) =>
                          handlePdfDownload(
                            event,
                            attachment.file,
                            attachment.name || `attachment_${index + 1}`
                          )
                        }
                      >
                        Download
                      </Button>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mt: 2, mb: 2, px: 4 }}>
          {/* Stage Images Section */}
          <Grid item xs={12}>
            {property.propertyStageImages && property.propertyStageImages.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'poppinsSemiBold',
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 2
                  }}
                >
                  <VideoIcon color="primary" />
                  Property Stage Images ({property.propertyStageImages.length})
                </Typography>

                {property.propertyStageImages.map((stage, stageIndex) => (
                  <Box key={stage.id} sx={{ mb: 4 }}>
                    {/* Stage Header */}
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontFamily: 'poppinsSemiBold',
                        mb: 2,
                        color: palette.primary.main,
                        fontSize: '16px'
                      }}
                    >
                      Property Stage image
                    </Typography>

                    <Grid container spacing={3} alignItems="center">
                      {/* From Image - Left Side */}
                      <Grid item xs={12} md={6}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Box
                            sx={{
                              height: 300,
                              width: '100%',
                              backgroundColor: palette.grey[200],
                              borderRadius: 2,
                              overflow: 'hidden',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: 2
                            }}
                          >
                            {stage.from_image && stage.from_image.file ? (
                              <img
                                src={stage.from_image.file}
                                alt={`Before Stage ${stageIndex + 1}`}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <Box
                              sx={{
                                width: '100%',
                                height: '100%',
                                display:
                                  stage.from_image && stage.from_image.file ? 'none' : 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: palette.grey[300],
                                flexDirection: 'column'
                              }}
                            >
                              <HomeIcon
                                sx={{ fontSize: 40, color: palette.text.disabled, mb: 1 }}
                              />
                              <Typography variant="body2" color="textSecondary">
                                No Before Image
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Grid>

                      {/* To Images - Right Side */}
                      <Grid item xs={12} md={6}>
                        <Box sx={{ textAlign: 'center' }}>
                          {stage.to_images && stage.to_images.length > 0 ? (
                            <Grid container spacing={2}>
                              {stage.to_images.map((toImage, toIndex) => (
                                <Grid item xs={12} sm={12} key={toImage.id}>
                                  <Box
                                    sx={{
                                      height: 300,
                                      width: '100%',
                                      backgroundColor: palette.grey[200],
                                      borderRadius: 2,
                                      overflow: 'hidden',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      boxShadow: 2
                                    }}
                                  >
                                    <img
                                      src={toImage.file}
                                      alt={`After Stage ${stageIndex + 1} - Image ${toIndex + 1}`}
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                      }}
                                    />
                                    <Box
                                      sx={{
                                        width: '100%',
                                        height: '100%',
                                        display: 'none',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: palette.grey[300],
                                        flexDirection: 'column'
                                      }}
                                    >
                                      <HomeIcon
                                        sx={{ fontSize: 30, color: palette.text.disabled, mb: 1 }}
                                      />
                                      <Typography variant="caption" color="textSecondary">
                                        No Image
                                      </Typography>
                                    </Box>
                                  </Box>
                                  {stage.to_images.length > 1 && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontFamily: 'poppinsMedium',
                                        color: palette.text.secondary,
                                        mt: 0.5
                                      }}
                                    >
                                      Image {toIndex + 1}
                                    </Typography>
                                  )}
                                </Grid>
                              ))}
                            </Grid>
                          ) : (
                            <Box
                              sx={{
                                height: 300,
                                backgroundColor: palette.grey[200],
                                borderRadius: 2,
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: 2
                              }}
                            >
                              <Box
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: palette.grey[300],
                                  flexDirection: 'column'
                                }}
                              >
                                <HomeIcon
                                  sx={{ fontSize: 40, color: palette.text.disabled, mb: 1 }}
                                />
                                <Typography variant="body2" color="textSecondary">
                                  No After Images
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Divider between stages */}
                    {stageIndex < property.propertyStageImages.length - 1 && (
                      <Divider sx={{ my: 3 }} />
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Grid>
        </Grid>

        {/* Timestamps */}
        <Divider sx={{ my: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CalendarIcon sx={{ color: palette.primary.main, mr: 1.5 }} />
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'poppinsMedium',
                    color: palette.text.secondary,
                    fontSize: '13px'
                  }}
                >
                  Created At
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'poppinsSemiBold',
                    color: palette.text.primary,
                    fontSize: '14px'
                  }}
                >
                  {formatDate(property.created_at)}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CalendarIcon sx={{ color: palette.primary.main, mr: 1.5 }} />
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'poppinsMedium',
                    color: palette.text.secondary,
                    fontSize: '13px'
                  }}
                >
                  Last Updated
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'poppinsSemiBold',
                    color: palette.text.primary,
                    fontSize: '14px'
                  }}
                >
                  {formatDate(property.updated_at)}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: `1px solid ${palette.grey[200]}` }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: palette.primary.main,
            color: palette.primary.contrastText,
            fontFamily: 'poppinsSemiBold',
            px: 3,
            py: 1,
            borderRadius: 1,
            '&:hover': {
              backgroundColor: palette.primary.dark,
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            },
            transition: 'all 0.2s ease'
          }}
        >
          Close Details
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PropertyViewModal;
